<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useInfiniteScroll } from '@vueuse/core';
import type { Video } from '@ilteducation/videos';
import useLocale from '../../../composables/use-locale';

defineProps<{
  searchInput: string;
  isLoading: boolean;
  noQuizWarning: string | null;
  filteredResults: Video[];
  checkingVideoId: string | null;
  isVideoAdded: (resourceId: string) => boolean;
}>();

const emit = defineEmits<{
  (e: 'update:searchInput', value: string): void;
  (e: 'search', value: string): void;
  (e: 'closeWarning'): void;
  (e: 'addVideo', video: Video): void;
  (e: 'loadMore'): void;
}>();

const contentArea = ref<HTMLElement | null>(null);

onMounted(() => {
  if (contentArea.value) {
    useInfiniteScroll(contentArea, () => emit('loadMore'), { distance: 20 });
  }
});

const { t } = useLocale();

const handleSearchUpdate = (value: string | null | undefined) => {
  const strValue = value ?? '';
  emit('update:searchInput', strValue);
  emit('search', strValue);
};

const handleClear = () => {
  emit('update:searchInput', '');
};
</script>

<template>
  <div class="d-flex flex-column">
    <!-- Search Field -->
    <v-text-field
      :model-value="searchInput"
      :label="t('components.search')"
      variant="outlined"
      density="comfortable"
      rounded="default"
      hide-details
      clearable
      @update:model-value="handleSearchUpdate"
      @click:clear="handleClear"
    >
      <template #append-inner>
        <v-icon icon="fa:fawsb fa-magnifying-glass"></v-icon>
      </template>
    </v-text-field>

    <!-- Warning message for videos without quizzes -->
    <v-alert v-if="noQuizWarning" type="warning" variant="flat" closable @click:close="emit('closeWarning')">
      {{ t('quizter.video_no_quiz_warning', { title: noQuizWarning }) }}
    </v-alert>

    <div ref="contentArea" class="video-content-area mt-4">
      <!-- Initial Loading State (no results yet) -->
      <div v-if="isLoading && filteredResults.length === 0" class="d-flex justify-center align-center pa-8">
        <v-progress-circular indeterminate></v-progress-circular>
      </div>

      <!-- Video Results (kept mounted during pagination loads to preserve scroll) -->
      <v-list v-if="filteredResults.length > 0" class="video-list bg-transparent overflow-x-hidden" lines="two">
        <v-list-item v-for="video in filteredResults" :key="video.resourceId" class="video-item">
          <template #prepend>
            <div class="video-thumbnail d-flex align-center justify-center rounded">
              <v-img
                v-if="video.thumbnail || video.previewImage"
                :src="video.thumbnail || video.previewImage"
                cover
                class="rounded"
              ></v-img>
              <v-icon v-else icon="fa:fawsb fa-video" size="large"></v-icon>
            </div>
          </template>
          <v-list-item-title class="text-truncate">
            {{ video.title }}
          </v-list-item-title>
          <template #append>
            <div class="d-flex justify-end" style="min-width: 80px">
              <div
                v-if="isVideoAdded(video.resourceId)"
                class="append-icon d-flex align-center justify-center opacity-80"
              >
                <v-icon icon="fa:fawsb fa-check-circle" color="success" size="small"></v-icon>
              </div>
              <v-btn
                v-else
                icon
                size="small"
                variant="plain"
                class="add-video-btn"
                :loading="checkingVideoId === video.resourceId"
                :disabled="checkingVideoId === video.resourceId"
                :aria-label="t('components.add_button_label', { title: video.title })"
                @click.stop="emit('addVideo', video)"
              >
                <v-icon icon="fa:fawsb fa-plus" size="xsmall"></v-icon>
              </v-btn>
            </div>
          </template>
        </v-list-item>
      </v-list>

      <!-- Pagination Loading Spinner -->
      <div v-if="isLoading && filteredResults.length > 0" class="d-flex justify-center pa-4">
        <v-progress-circular indeterminate size="24"></v-progress-circular>
      </div>

      <!-- No Results -->
      <div v-if="!isLoading && filteredResults.length === 0 && searchInput" class="text-center pa-8 opacity-70">
        {{ t('search.no_video_search_result') }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.video-content-area {
  height: calc(90vh - 285px);
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(var(--v-theme-on-surface), 0.3);
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.5);
  }
}

.video-list {
  .append-icon {
    width: 40px;
    height: 40px;
  }
}

.add-video-btn:focus-visible {
  outline: 2px solid rgb(var(--v-theme-on-surface));
  outline-offset: 2px;
}

.video-item {
  transition: background-color 0.2s;
  min-height: 72px;
  height: 72px;

  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.1);
  }

  :deep(.v-list-item__prepend) {
    margin-right: 16px;
  }
}

.video-thumbnail {
  width: 100px;
  height: 56px;
  background-color: rgba(var(--v-theme-on-surface), 0.05);

  .v-img {
    width: 100%;
    height: 100%;
  }
}
</style>
